import ImagePreview from '@/components/ImagePreview';
import LoadingCircular from '@/components/LoadingCircular';
import { toast } from '@/components/Toast';
import { awsUpload } from '@/utils/aws-s3';
import logger from '@/utils/client-logger';
import { useTranslations } from '@/i18n/use-translations';
import { useCallback, useEffect, useState } from 'react';

const ALLOWED_IMAGE_TYPES = ['.jpeg', '.jpg', '.png'];
const DEFAULT_MAX_SIZE = 10; // 默认10MB

export default function UploadPrescriptionImg({
  className,
  onChange,
  img,
  children,
  maxSize = DEFAULT_MAX_SIZE,
  onDelete,
  allowDelete = true,
  width = 48,
  height = 48,
  SDMyorderDetailsPageBtnClick,
}: {
  className?: string;
  onChange?: (path: string) => void;
  img?: string;
  children?: React.ReactNode;
  maxSize?: number;
  onDelete?: () => void;
  allowDelete?: boolean;
  width?: number;
  height?: number;
  SDMyorderDetailsPageBtnClick?: (btn_name: string) => void;
}) {
  const t = useTranslations('account.myPrescriptions.upload');
  const [isDragging, setIsDragging] = useState(false);
  const [currentImg, setCurrentImg] = useState(img);
  const [loading, setLoading] = useState(false);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);

  useEffect(() => {
    setCurrentImg(img);
  }, [img]);

  useEffect(() => {
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [blobUrl]);

  const validateFile = (file: File): boolean => {
    const fileExtension = `.${file.name.split('.').pop()?.toLowerCase()}`;
    if (!ALLOWED_IMAGE_TYPES.includes(fileExtension)) {
      toast.error(t('onlySupports'));
      return false;
    }
    if (file.size > maxSize * 1024 * 1024) {
      toast.error(t('fileSizeLimit', { size: maxSize }));
      return false;
    }
    return true;
  };

  /**
   * 上传图片到 s3
   */
  const handleUpload = useCallback(
    async (file: File) => {
      if (!validateFile(file)) return;
      const tempBlobUrl = URL.createObjectURL(file);
      setBlobUrl(tempBlobUrl);
      setCurrentImg(tempBlobUrl);
      setLoading(true);
      try {
        const res = await awsUpload(2, file, (percent, { md5 }) => {
          logger.log(percent, md5);
        });
        if (res) {
          // const uploadSuccess = res as string;
          // setCurrentImg(res as string);
          setBlobUrl(null);
          onChange(res as string);
        }
      } catch {
        setCurrentImg(null);
        if (tempBlobUrl) URL.revokeObjectURL(tempBlobUrl);
        setBlobUrl(null);
        onChange(null);
      } finally {
        setLoading(false);
      }
    },
    [onChange, maxSize, validateFile],
  );

  const uploadImgClick = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = ALLOWED_IMAGE_TYPES.join(',');
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        handleUpload(file);
      }
    };
    input.click();
  }, [handleUpload]);

  const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const { files } = e.dataTransfer;
      if (files && files.length > 0) {
        const file = files[0];
        handleUpload(file);
      }
    },
    [handleUpload],
  );

  const handleDelete = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onDelete?.();
      onChange(null);
      setCurrentImg(null);
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
        setBlobUrl(null);
      }
    },
    [onDelete, onChange, blobUrl],
  );

  const handleImgClick = () => {
    SDMyorderDetailsPageBtnClick?.('Click or drag file upload');
  };

  return (
    <div className={`flex items-center ${className}`} onClick={handleImgClick}>
      {currentImg && !loading ? (
        <ImagePreview
          images={[currentImg]}
          width={width}
          height={height}
          allowDelete={allowDelete}
          onDelete={handleDelete}
        />
      ) : (
        <div
          onClick={uploadImgClick}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`relative transition-all ${isDragging ? 'border-dashed border-2 border-blue-500 bg-blue-50' : ''}`}
        >
          {loading && (
            <div className="absolute  top-0 left-0 w-full">
              <LoadingCircular
                loadingText=""
                wrapperClassName="!h-[56px] !leading-[56px]"
                size={16}
              />
            </div>
          )}
          {children}
        </div>
      )}
    </div>
  );
}
